import { observable, action, computed, makeObservable, runInAction } from 'mobx'
import dayjs from 'dayjs'
import { ItemAvailability } from '../Types/ItemAvailability'
import { FetchStatus, FetchResult } from '~shared/lib/Types/FetchTypes'
import type { IItemOption } from '~shared/lib/ItemDetail/Types'
import {
  fetchItemOptionsForSiteId,
  fetchCatalogItems,
  setItemAvailability as setItemAvailabilityApi,
  setItemOptionAvailability as setItemOptionAvailabilityApi,
  setItemOptionItemAvailability as setItemOptionItemAvailabilityApi,
} from '../Services/CatalogService'
import KdsAuthorizationStore from './KdsAuthorizationStore'
import { KdsActionId } from '../Types/KdsAuthorization'
import { KdsAuthorizationCancelledError } from '../Types/Authorization'

type ItemOptionsFetch = FetchResult<IItemOption[]>

/** How long before item options prefetch is considered stale and should be re-fetched */
const PREFETCH_STALE_MINUTES = 5

async function runWithConcurrencyLimit<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  const concurrency = Math.max(1, limit)
  let idx = 0

  const runners = Array.from({ length: Math.min(concurrency, items.length) }).map(async () => {
    while (idx < items.length) {
      const currentIndex = idx++
      const item = items[currentIndex]
      await worker(item)
    }
  })

  await Promise.all(runners)
}

class CatalogStore {
  catalogFetch: FetchResult<ItemAvailability[]> = {
    status: FetchStatus.unloaded,
    lastFetch: null,
    data: undefined,
  }
  /**
   * Currently active site+department request key.
   * Used to prevent out-of-order fetches from overwriting the visible catalog.
   */
  activeCatalogKey: string | null = null

  itemOptionsByItemId: Map<string, IItemOption[]> = new Map()
  itemOptionsFetchByItemId: Map<string, ItemOptionsFetch> = new Map()
  lastItemOptionsPrefetchKey: string | null = null
  lastItemOptionsPrefetchAt: string | null = null

  togglingItemOptionIds: Map<string, boolean> = new Map()
  togglingItemOptionItemIds: Map<string, boolean> = new Map()

  /** Currently toggling item ID (for item availability toggle) */
  togglingItemId: string | null = null

  /**
   * In-memory per-key cache (stale-while-revalidate).
   * Note: this is not persisted; a page refresh clears it.
   */
  private catalogCache = new Map<string, { lastFetch: string; data: ItemAvailability[] }>()

  /**
   * In-flight request de-dupe per key.
   */
  private inflightFetches = new Map<string, Promise<void>>()

  constructor() {
    makeObservable(this, {
      catalogFetch: observable,
      activeCatalogKey: observable,
      itemOptionsByItemId: observable,
      itemOptionsFetchByItemId: observable,
      lastItemOptionsPrefetchKey: observable,
      lastItemOptionsPrefetchAt: observable,
      togglingItemOptionIds: observable,
      togglingItemOptionItemIds: observable,
      togglingItemId: observable,
      fetchCatalog: action.bound,
      setItemAvailability: action.bound,
      fetchItemOptionsForItem: action.bound,
      prefetchItemOptionsForCatalog: action.bound,
      setItemOptionAvailability: action.bound,
      setItemOptionItemAvailability: action.bound,
      invalidate: action.bound,
      catalogTree: computed,
      unavailableItemsCount: computed,
      items: computed,
      isLoading: computed,
      isInitialLoading: computed,
      hasError: computed,
      isEmpty: computed,
    })
  }

  /**
   * Fetch department items/availability for a specific site + department
   */
  async fetchCatalog(siteId: string, departmentId: string): Promise<void> {
    const requestKey = `${siteId}:${departmentId}`
    const cached = this.catalogCache.get(requestKey)

    runInAction(() => {
      this.activeCatalogKey = requestKey
      this.catalogFetch = {
        status: FetchStatus.fetching,
        lastFetch: dayjs().format(),
        // SWR: keep any cached data visible while we revalidate.
        data: cached?.data,
      }
    })

    const existingInflight = this.inflightFetches.get(requestKey)
    if (existingInflight) {
      await existingInflight
      // Non-blocking: prefetch item options for current catalog (if not recently done).
      void this.prefetchItemOptionsForCatalog(siteId)
      return
    }

    const fetchPromise = (async () => {
      try {
        const data = await fetchCatalogItems(siteId, departmentId)

        // NOTE: API uses PascalCase JSON (DefaultContractResolver). Keep shape as-is and
        // trickle PascalCase down through the UI (no camelCase mapping).
        const items = (data ?? []) as ItemAvailability[]
        const now = dayjs().format()

        runInAction(() => {
          this.catalogCache.set(requestKey, { lastFetch: now, data: items })

          // Only update the visible catalog if this key is still the active request.
          if (this.activeCatalogKey === requestKey) {
            this.catalogFetch = {
              status: FetchStatus.success,
              lastFetch: now,
              data: items,
            }
          }
        })
      } catch (error: any) {
        console.error('Failed to fetch department catalog:', error)
        runInAction(() => {
          // SWR: if we already have *any* data (even empty list), keep it visible and
          // don't switch the UI into a hard error state.
          const hasAnyData = this.catalogFetch.data !== undefined

          if (!hasAnyData && this.activeCatalogKey === requestKey) {
            this.catalogFetch = {
              ...this.catalogFetch,
              status: FetchStatus.error,
            }
          } else if (this.activeCatalogKey === requestKey) {
            this.catalogFetch = {
              ...this.catalogFetch,
              status: FetchStatus.success,
            }
          }
        })
      } finally {
        this.inflightFetches.delete(requestKey)
      }
    })()

    this.inflightFetches.set(requestKey, fetchPromise)
    await fetchPromise

    // Non-blocking: prefetch item options for current catalog (if not recently done).
    void this.prefetchItemOptionsForCatalog(siteId)
  }

  /**
   * Prefetch item options for every item currently in the catalog.
   * Empty array response is treated as "no options".
   */
  async prefetchItemOptionsForCatalog(siteId: string): Promise<void> {
    // Derive siteId from activeCatalogKey to avoid stale values if the catalog changed
    const effectiveSiteId = this.activeCatalogKey ? this.activeCatalogKey.split(':')[0] : siteId
    const requestKey = this.activeCatalogKey
      ? `options:${this.activeCatalogKey}`
      : `options:${effectiveSiteId}`

    // If we prefetched recently for this catalog key, skip
    if (
      this.lastItemOptionsPrefetchKey === requestKey &&
      this.lastItemOptionsPrefetchAt &&
      dayjs().diff(dayjs(this.lastItemOptionsPrefetchAt), 'minute') < PREFETCH_STALE_MINUTES
    ) {
      return
    }

    const itemIds = (this.catalogFetch.data ?? []).map(i => i.ItemUid).filter(Boolean)
    if (itemIds.length === 0) {
      runInAction(() => {
        this.lastItemOptionsPrefetchKey = requestKey
        this.lastItemOptionsPrefetchAt = dayjs().format()
      })
      return
    }

    runInAction(() => {
      this.lastItemOptionsPrefetchKey = requestKey
      this.lastItemOptionsPrefetchAt = dayjs().format()
    })

    await runWithConcurrencyLimit(itemIds, 8, async itemId => {
      const existing = this.itemOptionsFetchByItemId.get(itemId)
      if (existing?.status === FetchStatus.success || existing?.status === FetchStatus.fetching) {
        return
      }

      runInAction(() => {
        this.itemOptionsFetchByItemId.set(itemId, {
          status: FetchStatus.fetching,
          lastFetch: dayjs().format(),
          data: this.itemOptionsByItemId.get(itemId),
        })
      })

      try {
        const options = await fetchItemOptionsForSiteId(itemId, effectiveSiteId)

        runInAction(() => {
          this.itemOptionsByItemId.set(itemId, options)
          this.itemOptionsFetchByItemId.set(itemId, {
            status: FetchStatus.success,
            lastFetch: dayjs().format(),
            data: options,
          })
        })
      } catch (error: unknown) {
        console.error(`Failed to prefetch item options for item ${itemId}:`, error)
        runInAction(() => {
          this.itemOptionsFetchByItemId.set(itemId, {
            status: FetchStatus.error,
            lastFetch: dayjs().format(),
            data: this.itemOptionsByItemId.get(itemId),
          })
        })
      }
    })
  }

  /**
   * Ensure item options are fetched for a specific item (deduped).
   */
  async fetchItemOptionsForItem(siteId: string, itemId: string): Promise<void> {
    const existing = this.itemOptionsFetchByItemId.get(itemId)
    if (existing?.status === FetchStatus.success || existing?.status === FetchStatus.fetching) {
      return
    }

    runInAction(() => {
      this.itemOptionsFetchByItemId.set(itemId, {
        status: FetchStatus.fetching,
        lastFetch: dayjs().format(),
        data: this.itemOptionsByItemId.get(itemId),
      })
    })

    try {
      const options = await fetchItemOptionsForSiteId(itemId, siteId)

      runInAction(() => {
        this.itemOptionsByItemId.set(itemId, options)
        this.itemOptionsFetchByItemId.set(itemId, {
          status: FetchStatus.success,
          lastFetch: dayjs().format(),
          data: options,
        })
      })
    } catch (error: unknown) {
      console.error(`Failed to fetch item options for item ${itemId}:`, error)
      runInAction(() => {
        this.itemOptionsFetchByItemId.set(itemId, {
          status: FetchStatus.error,
          lastFetch: dayjs().format(),
          data: this.itemOptionsByItemId.get(itemId),
        })
      })
    }
  }

  /**
   * Toggle item option daily availability for a site (today in site's timezone).
   * Optimistic update across any cached item options lists that include this option.
   */
  async setItemOptionAvailability(
    siteId: string,
    itemId: string,
    itemOptionId: string,
    available: boolean
  ): Promise<void> {
    if (!siteId || !itemId || !itemOptionId) return
    if (this.togglingItemOptionIds.get(itemOptionId)) return

    const previousOptionsByItemId: Map<string, IItemOption[]> = new Map()

    runInAction(() => {
      this.togglingItemOptionIds.set(itemOptionId, true)

      // Only update the specific item's options (not all items using this option)
      const options = this.itemOptionsByItemId.get(itemId)
      if (options) {
        const hasMatch = options.some(o => o?.id === itemOptionId)
        if (hasMatch) {
          previousOptionsByItemId.set(itemId, options)

          const nextOptions = options.map(o =>
            o?.id === itemOptionId ? { ...o, IsAvailableToday: available } : o
          )

          this.itemOptionsByItemId.set(itemId, nextOptions)
          const existingFetch = this.itemOptionsFetchByItemId.get(itemId)
          if (existingFetch?.status === FetchStatus.success) {
            this.itemOptionsFetchByItemId.set(itemId, { ...existingFetch, data: nextOptions })
          }
        }
      }
    })

    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.EditItemAvailability,
        () => setItemOptionAvailabilityApi(siteId, itemId, itemOptionId, available),
        authorization =>
          setItemOptionAvailabilityApi(siteId, itemId, itemOptionId, available, authorization.token)
      )

      // Silently refresh catalog to get updated item availability (e.g., when required options become unavailable)
      if (this.activeCatalogKey) {
        const [catalogSiteId, departmentId] = this.activeCatalogKey.split(':')
        void this.fetchCatalog(catalogSiteId, departmentId)
      }
    } catch (error: unknown) {
      runInAction(() => {
        for (const [prevItemId, prev] of previousOptionsByItemId.entries()) {
          this.itemOptionsByItemId.set(prevItemId, prev)
          const existingFetch = this.itemOptionsFetchByItemId.get(prevItemId)
          if (existingFetch?.status === FetchStatus.success) {
            this.itemOptionsFetchByItemId.set(prevItemId, { ...existingFetch, data: prev })
          }
        }
      })
      // A dismissed PIN rolls back like any failure, but it isn't an error to report.
      if (error instanceof KdsAuthorizationCancelledError) return
      console.error(`Failed to set item option availability for option ${itemOptionId}:`, error)
      throw error
    } finally {
      runInAction(() => {
        this.togglingItemOptionIds.set(itemOptionId, false)
      })
    }
  }

  /**
   * Toggle item option item daily availability for a site (today in site's timezone).
   * Optimistic update across any cached item options lists that include this option item.
   */
  async setItemOptionItemAvailability(
    siteId: string,
    itemId: string,
    itemOptionItemId: string,
    available: boolean
  ): Promise<void> {
    if (!siteId || !itemId || !itemOptionItemId) return
    if (this.togglingItemOptionItemIds.get(itemOptionItemId)) return

    const previousOptionsByItemId: Map<string, IItemOption[]> = new Map()

    runInAction(() => {
      this.togglingItemOptionItemIds.set(itemOptionItemId, true)

      // Only update the specific item's options (not all items using this option item)
      const options = this.itemOptionsByItemId.get(itemId)
      if (options) {
        const hasMatch = options.some(o => o?.Items?.some(oi => oi?.id === itemOptionItemId))
        if (hasMatch) {
          previousOptionsByItemId.set(itemId, options)

          const nextOptions = options.map(o => ({
            ...o,
            Items: (o.Items ?? []).map(oi =>
              oi?.id === itemOptionItemId ? { ...oi, IsAvailableToday: available } : oi
            ),
          }))

          this.itemOptionsByItemId.set(itemId, nextOptions)
          const existingFetch = this.itemOptionsFetchByItemId.get(itemId)
          if (existingFetch?.status === FetchStatus.success) {
            this.itemOptionsFetchByItemId.set(itemId, { ...existingFetch, data: nextOptions })
          }
        }
      }
    })

    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.EditItemAvailability,
        () => setItemOptionItemAvailabilityApi(siteId, itemId, itemOptionItemId, available),
        authorization =>
          setItemOptionItemAvailabilityApi(
            siteId,
            itemId,
            itemOptionItemId,
            available,
            authorization.token
          )
      )

      // Silently refresh catalog to get updated item availability (e.g., when required options become unavailable)
      if (this.activeCatalogKey) {
        const [catalogSiteId, departmentId] = this.activeCatalogKey.split(':')
        void this.fetchCatalog(catalogSiteId, departmentId)
      }
    } catch (error: unknown) {
      runInAction(() => {
        for (const [prevItemId, prev] of previousOptionsByItemId.entries()) {
          this.itemOptionsByItemId.set(prevItemId, prev)
          const existingFetch = this.itemOptionsFetchByItemId.get(prevItemId)
          if (existingFetch?.status === FetchStatus.success) {
            this.itemOptionsFetchByItemId.set(prevItemId, { ...existingFetch, data: prev })
          }
        }
      })
      // A dismissed PIN rolls back like any failure, but it isn't an error to report.
      if (error instanceof KdsAuthorizationCancelledError) return
      console.error(
        `Failed to set item option item availability for optionItem ${itemOptionItemId}:`,
        error
      )
      throw error
    } finally {
      runInAction(() => {
        this.togglingItemOptionItemIds.set(itemOptionItemId, false)
      })
    }
  }

  /**
   * Toggle item daily availability for a site (today in site's timezone).
   * Manages togglingItemId state internally.
   */
  async setItemAvailability(siteId: string, itemId: string, available: boolean): Promise<void> {
    if (this.togglingItemId) return // Already toggling

    runInAction(() => {
      this.togglingItemId = itemId
    })

    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.EditItemAvailability,
        () => setItemAvailabilityApi(siteId, itemId, available),
        authorization => setItemAvailabilityApi(siteId, itemId, available, authorization.token)
      )

      runInAction(() => {
        const current = this.catalogFetch.data ?? []
        const updated = current.map(i =>
          i.ItemUid === itemId ? { ...i, IsAvailableToday: available } : i
        )
        this.catalogFetch = {
          ...this.catalogFetch,
          data: updated,
        }

        if (this.activeCatalogKey) {
          const cached = this.catalogCache.get(this.activeCatalogKey)
          if (cached) {
            this.catalogCache.set(this.activeCatalogKey, {
              ...cached,
              data: updated,
            })
          }
        }
      })
    } catch (error) {
      // State only changes after a successful call, so a dismissed PIN needs no rollback.
      if (error instanceof KdsAuthorizationCancelledError) return
      throw error
    } finally {
      runInAction(() => {
        this.togglingItemId = null
      })
    }
  }

  /**
   * Invalidate cache to force refetch on next access
   */
  invalidate(): void {
    this.catalogFetch = {
      status: FetchStatus.unloaded,
      lastFetch: null,
      data: undefined,
    }
    this.activeCatalogKey = null
    this.catalogCache.clear()
    this.inflightFetches.clear()
    this.itemOptionsByItemId.clear()
    this.itemOptionsFetchByItemId.clear()
    this.lastItemOptionsPrefetchKey = null
    this.lastItemOptionsPrefetchAt = null
    this.togglingItemOptionIds.clear()
    this.togglingItemOptionItemIds.clear()
    this.togglingItemId = null
  }

  /**
   * Get catalog items
   */
  get items(): ItemAvailability[] {
    return this.catalogFetch.data ?? []
  }

  /**
   * Get hierarchical tree structure (items with nested options)
   */
  get catalogTree(): ItemAvailability[] {
    // Return items directly as the tree structure
    return this.items
  }

  /**
   * Count of unavailable items
   */
  get unavailableItemsCount(): number {
    return this.items.filter(item => !item.IsAvailableToday).length
  }

  /**
   * Whether the catalog is currently being fetched
   */
  get isLoading(): boolean {
    return this.catalogFetch.status === FetchStatus.fetching
  }

  /**
   * Whether this is the initial load (no cached data yet)
   */
  get isInitialLoading(): boolean {
    return this.isLoading && this.catalogFetch.data === undefined
  }

  /**
   * Whether the catalog fetch resulted in an error
   */
  get hasError(): boolean {
    return this.catalogFetch.status === FetchStatus.error
  }

  /**
   * Whether the catalog loaded successfully but is empty
   */
  get isEmpty(): boolean {
    return this.catalogFetch.status === FetchStatus.success && this.items.length === 0
  }

  /**
   * Check if a specific item is currently being toggled
   */
  isTogglingItem(itemId: string): boolean {
    return this.togglingItemId === itemId
  }

  /**
   * Get a catalog item by its ID
   */
  getItemById(itemId: string): ItemAvailability | undefined {
    return this.items.find(item => item.ItemUid === itemId)
  }

  /**
   * Get items filtered by availability
   */
  getItemsByAvailability(available: boolean): ItemAvailability[] {
    return this.items.filter(item => item.IsAvailableToday === available)
  }
}

const catalogStore = new CatalogStore()
export default catalogStore
export { CatalogStore }
